<template>
  <v-dialog v-model="open" width="880" persistent>
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div>
          <v-btn icon variant="text" @click="emit('close')">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="ml-2">Permisos rol “{{ role?.name }}”</span>
        </div>
        <div>
          <v-btn color="primary" :loading="saving" @click="save">Guardar</v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="q"
          density="compact"
          variant="solo-filled"
          prepend-inner-icon="mdi-magnify"
          label="Buscar permisos o categorías"
          class="mb-3"
        />

        <div
          v-for="(group, idx) in filtered"
          :key="idx"
          class="py-3"
          style="border-bottom: 1px solid rgba(0,0,0,.08)"
        >
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="text-subtitle-1 font-weight-medium">
              {{ group.category }}
            </div>
            <div class="d-flex align-center">
              <v-btn size="x-small" variant="text" @click="selectAll(group.permissions)">Seleccionar todo</v-btn>
              <v-btn size="x-small" variant="text" @click="clear(group.permissions)">Limpiar</v-btn>
            </div>
          </div>

          <div class="d-flex flex-wrap">
            <v-chip
              v-for="perm in group.permissions"
              :key="perm.name"
              class="ma-1"
              :variant="isSelected(perm.name) ? 'elevated' : 'outlined'"
              @click="toggle(perm.name)"
              label
              size="small"
            >
              {{ perm.label }}
            </v-chip>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRolesStore } from '@/store/roles';

const props = defineProps<{ role: any }>();
const emit = defineEmits<{ (e:'close'): void }>();

const rolesStore = useRolesStore();
const open = ref(true);
const saving = ref(false);

const q = ref('');
const selected = ref<string[]>([]);

onMounted(async () => {
  // cargar permisos si no están
  if (!rolesStore.permissions?.length) {
    await rolesStore.fetchPermissions();
  }
  // permisos actuales del rol (si viene con include=permissions)
  selected.value = (props.role?.permissions || []).map((p: any) => p.name);
});

watch(() => props.role, (r) => {
  selected.value = (r?.permissions || []).map((p: any) => p.name);
});

const grouped = computed(() => {
  const groups: Record<string, { category: string; permissions: any[] }> = {};
  for (const p of rolesStore.permissions) {
    const cat = p.category || 'General';
    if (!groups[cat]) groups[cat] = { category: cat, permissions: [] };
    groups[cat].permissions.push(p);
  }
  // a array
  return Object.values(groups).sort((a, b) => a.category.localeCompare(b.category));
});

const filtered = computed(() => {
  const term = q.value.trim().toLowerCase();
  if (!term) return grouped.value;
  return grouped.value
    .map(g => ({
      category: g.category,
      permissions: g.permissions.filter((p:any) =>
        p.label?.toLowerCase().includes(term) ||
        p.name?.toLowerCase().includes(term) ||
        g.category?.toLowerCase().includes(term)
      )
    }))
    .filter(g => g.permissions.length);
});

function isSelected(name: string) {
  return selected.value.includes(name);
}
function toggle(name: string) {
  if (isSelected(name)) {
    selected.value = selected.value.filter(n => n !== name);
  } else {
    selected.value = [...selected.value, name];
  }
}
function selectAll(perms: any[]) {
  const names = perms.map(p => p.name);
  const set = new Set([...selected.value, ...names]);
  selected.value = Array.from(set);
}
function clear(perms: any[]) {
  const names = perms.map(p => p.name);
  selected.value = selected.value.filter(n => !names.includes(n));
}

async function save() {
  if (!props.role?.id) return;
  try {
    saving.value = true;
    await rolesStore.syncRolePermissions(props.role.id, selected.value);
    emit('close');
  } finally {
    saving.value = false;
  }
}
</script>
